import React from 'react';
import styles from './knowledge-binder.module.scss';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import {
  SEARCH_TYPES_KEYS,
  Lang,
  KNOWLEDGE_TYPES,
  ROUTES,
  INTERNAL_ROUTING,
  CATEGORY_TYPES,
  REQUEST_ACCESS_ENTITIES,
} from 'shared/constants';
import { isCollaborator } from 'shared/store/selectors/auth.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getSelectedWorkspace } from 'shared/store/selectors/workspace.selector';
import { useExpiryFilterWithAdmin } from 'shared/hooks/knowledge.hooks';
import { CategoryMirrorBadge } from '../knowledge/components/mirror/category-mirror-badge.component';
import { LocalItemsSeparator } from '../knowledge/components/mirror/local-items-separator.component';
import { useMirroredInTargets } from '../knowledge/components/mirror/use-mirrored-in-targets';
import { Loader } from '../../../components/core';
import { KnowledgeDeletePopup } from 'src/components/knowledge-content/knowledge-content-editor';
import update from 'immutability-helper';
import { useDrop, useDragLayer } from 'react-dnd';
import { SearchType } from '../../../components/searchType';
import { KnowledgeMovePopup } from '../knowledge/components/knowledge-row/knowledgeMove';
import { normalizedContains } from 'shared/utils/Utils';
import { formatNumber } from 'shared/utils';
import {
  isExternalSyncReadOnly,
  getFieldVisibility,
  KNOWLEDGE_VISIBILITY_FIELDS,
} from 'shared/utils/knowledge.utils';
import { TagsIcons } from '../../../components/tags/tagsIcons';
import { KnowledgeWrapperComponent } from '../knowledge/components/knowledge-row';
import {
  KnowledgeLinkEditor,
  KnowledgeLinkDeletePopup,
  KnowledgeLinkUpdatePopup,
  KnowledgeLinkMovePopup,
} from '../knowledge/components/knowledge-link-editor';
import { TextTag } from '../../../components/tags/text-tags';
import {
  KnowledgeAddDropdown,
  KnowledgeUploadFileComponent,
} from '../../../components/knowledge-content';
import { KnowledgeUploadExternalLinkComponent } from '../../../components/knowledge-content/knowledge-upload-external-link.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { getExternalSyncIcon } from '../../../constants/external-sync-icons-mapper';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import { assertPermission } from 'src/components/common/access-restricted/assert-permission';
import { usePageTitle } from 'src/components/app/use-page-title';
import { Button, ContentBinderDrawer, SearchBox } from 'src/components/design-system';
import { Owners } from 'src/components/common/owners';
import { NotFound } from 'src/components/common';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { usePreloadVisibleContent } from 'src/hooks/use-preload-visible-content';
import { useProgressiveList } from 'src/hooks/use-progressive-list';
import { useLiveRegion } from 'src/hooks/accessibility/use-live-region';

const ROWS_PER_PAGE = 60;

const KnowledgeBinderComponent = React.memo(function KnowledgeBinderComponent({
  id,
  title = '',
  subtitle,
  options,
  showExternalSync = false,
  knowledgeItems = [],
  knowledgeSelectItem = () => {},
  initialOpenState = false,
  adminMode = false,
  updateKnowledgeItemsOrder = () => {},
  ownerIds = [],
  tags,
  labels,
  mirroredFrom,
  mirroredFromInfo,
  lang = Lang.KNOWLEDGE_BUTTONS,
  langTypes = Lang.SEARCH_TYPES,
  langInfo = Lang.INFORMATION_MODAL,
  routingParam = INTERNAL_ROUTING.QUERY_PARAMS.KNOWLEDGE,
  sharePath = null,
  loading = false,
  loadError = false,
  onRetry = () => {},
  itemConfig = {},
  skipPermissionCheck = false,
}) {
  const isCollaboratorUser = useSelector(isCollaborator);
  const mirrorLang = useSelector(getLang('KNOWLEDGE_CATEGORY'));
  const selectedWorkspaceName = useSelector(getSelectedWorkspace)?.name ?? '';
  const separatorLabel = mirrorLang.LOCAL_ITEMS_SEPARATOR?.replace(
    '{workspace}',
    selectedWorkspaceName,
  );
  const mirroredInTargets = useMirroredInTargets(id);
  const { search } = useLocation();
  const [isOpen, setOpenState] = React.useState(initialOpenState);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchType, setSearchType] = React.useState(null);
  const [localOrder, setLocalOrder] = React.useState(null); // Only for drag-and-drop reordering

  const initialIdRef = React.useRef(id);
  const [animated, setAnimated] = React.useState(() => !initialIdRef.current);

  const [deleteKnowledgeItem, setDeleteKnowledgeItem] = React.useState(null);
  // Removed knowledgeEntityEditor state - now using navigation
  const navigate = useNavigate();
  const [knowledgeEntityMove, setKnowledgeEntityMove] = React.useState(null);
  const [linkedItemEditor, setLinkedItemEditor] = React.useState(null);
  const [deleteLink, setDeleteLink] = React.useState(null);
  const [updateLink, setUpdateLink] = React.useState(null);
  const [moveLink, setMoveLink] = React.useState(null);
  const [uploadFile, setUploadFile] = React.useState(null);
  const [isUploadFileOpen, setIsUploadFileOpen] = React.useState(false);
  const [uploadExternalLink, setUploadExternalLink] = React.useState(null);
  const { externalSyncProvider = null } = options ?? {};
  const isReadOnly = isExternalSyncReadOnly(options);
  const shouldShowField = getFieldVisibility({ options });

  const expiryFilter = useExpiryFilterWithAdmin({ adminMode });

  const [notificationApi, contextHolder] = notification.useNotification();

  const { updateSubtitle } = usePageTitle();

  React.useEffect(() => {
    updateSubtitle(title);
  }, [title, updateSubtitle]);

  React.useEffect(() => {
    if (id && !skipPermissionCheck) {
      assertPermission({
        serviceMethod: async () => await knowledgeServiceApi.getKnowledgeCategoryById({ id }),
        entity: REQUEST_ACCESS_ENTITIES.KNOWLEDGE_CATEGORY,
        id,
      });
    }
  }, [id, skipPermissionCheck]);

  // Drag and Drop
  const mediaAreaGroup = `media`;
  const documentsAreaGroup = `documents`;
  const [, mediaDropArea] = useDrop({ accept: mediaAreaGroup });
  const [, documentsDropArea] = useDrop({ accept: documentsAreaGroup });
  const draggingGroup = useDragLayer(monitor =>
    monitor.isDragging() ? monitor.getItem()?.type : null,
  );
  // Per-group snap-back drop refs for the local-items separator — hovering it
  // returns the dragged item to its starting position (separator isn't droppable).
  const snapBackToOriginal = ({ id: draggedId, originalIndex: draggedOriginalIndex }) => {
    dragItem(draggedId, draggedOriginalIndex);
  };
  const [, documentsSeparatorDrop] = useDrop({
    accept: documentsAreaGroup,
    canDrop: () => false,
    hover: snapBackToOriginal,
  });
  const [, mediaSeparatorDrop] = useDrop({
    accept: mediaAreaGroup,
    canDrop: () => false,
    hover: snapBackToOriginal,
  });

  // Use localOrder if set (during drag), otherwise use knowledgeItems
  const orderedItems = localOrder ?? knowledgeItems;

  const dragItem = (dragId, atIndex) => {
    const { item, index } = findItem(dragId);
    setLocalOrder(
      update(orderedItems, {
        $splice: [
          [index, 1],
          [atIndex, 0, item],
        ],
      }),
    );
  };

  const findItem = findId => {
    const item = orderedItems.find(i => `${i.id}` === findId);
    return {
      item,
      index: orderedItems.indexOf(item),
    };
  };

  const finishDrag = async itemId => {
    try {
      await updateKnowledgeItemsOrder({
        id,
        itemId,
        index: findItem(itemId).index,
      });
    } catch (err) {
      notification.error({
        message:
          err?.code === 'CANNOT_REORDER_MIRROR_CHILDREN'
            ? mirrorLang.CANNOT_REORDER_MIRROR_CHILDREN
            : mirrorLang.REORDER_FAILED,
        placement: 'bottomRight',
      });
      // Rollback optimistic reorder
      setLocalOrder(null);
    }
  };

  // Delete knowledge items
  const deleteKnowledgeAction = React.useCallback(({ id, title }) => {
    // TODO: analytics
    setDeleteKnowledgeItem({ id, title });
  }, []);

  const onFinishDelete = React.useCallback(() => {
    setDeleteKnowledgeItem(null);
  }, []);

  // Move knowledge items
  const moveKnowledgeAction = React.useCallback(({ id, title, ownerIds, thumbnail }) => {
    // TODO: analytics
    setKnowledgeEntityMove({ id, title, ownerIds, thumbnail });
  }, []);

  const onFinishMove = React.useCallback(() => {
    setKnowledgeEntityMove(null);
  }, []);

  const editOrCreateKnowledge = React.useCallback(
    ({ item = {} }) => {
      const targetPath = item.id
        ? ROUTES.KNOWLEDGE_ITEMS_EDIT.replace(':id', item.id)
        : ROUTES.KNOWLEDGE_ITEMS_NEW;
      const stateData = item.id
        ? {
            initialData: {
              ...item,
              tags,
              labels,
              subtitle,
              type: KNOWLEDGE_TYPES.CONTENT,
              parentOwnerIds: ownerIds,
              categoryId: id,
            },
          }
        : {};
      navigate(targetPath, { state: stateData });
    },
    [navigate, tags, labels, subtitle, ownerIds, id],
  );

  const createNewKnowledge = React.useCallback(
    (data = {}) => {
      navigate(ROUTES.KNOWLEDGE_ITEMS_NEW, {
        state: {
          initialData: {
            ...data,
            tags,
            labels,
            subtitle,
            type: KNOWLEDGE_TYPES.CONTENT,
            parentOwnerIds: ownerIds,
            categoryId: id,
          },
        },
      });
    },
    [navigate, tags, labels, subtitle, ownerIds, id],
  );

  // Link Items
  const linkKnowledge = React.useCallback(() => {
    setLinkedItemEditor({ parentOwnerIds: ownerIds });
  }, [ownerIds]);

  const onFinishLink = React.useCallback(() => {
    setLinkedItemEditor(null);
  }, []);

  const onDeleteLink = React.useCallback(
    ({ id: itemId, title }) => {
      setDeleteLink({ id: itemId, categoryId: id, title });
    },
    [id],
  );

  const onFinishDeleteLink = React.useCallback(() => {
    setDeleteLink(null);
  }, []);

  const onUpdateLink = React.useCallback(
    ({ itemId, linkOwnerIds }) => {
      setUpdateLink({ id: itemId, categoryId: id, linkOwnerIds });
    },
    [id],
  );

  const onFinishUpdateLink = React.useCallback(() => {
    setUpdateLink(null);
  }, []);

  const onMoveLink = React.useCallback(({ id, title, linkOwnerIds }) => {
    setMoveLink({ id, title, linkOwnerIds });
  }, []);

  const onFinishMoveLink = React.useCallback(() => {
    setMoveLink(null);
  }, []);

  const onUploadFile = React.useCallback(() => {
    setUploadFile({ categoryId: id, tags, labels, subtitle, parentOwnerIds: ownerIds });
    setIsUploadFileOpen(true);
  }, [ownerIds, tags, labels, subtitle, id]);

  const onFinishUploadFile = React.useCallback(() => {
    setIsUploadFileOpen(false);
    // Wait for the modal close animation
    setUploadFile(null);
  }, []);

  const onExternalUpload = React.useCallback(
    (data = {}) => {
      navigate(ROUTES.KNOWLEDGE_ITEMS_NEW, {
        state: {
          initialData: {
            ...data,
            ownerIds,
            tags,
            labels,
            subtitle,
            parentOwnerIds: ownerIds,
            categoryId: id,
          },
        },
      });
    },
    [navigate, ownerIds, tags, labels, subtitle, id],
  );

  const onUploadExternalLink = React.useCallback(
    (data = {}) => {
      setUploadExternalLink({
        ...data,
        ownerIds,
        tags,
        labels,
        subtitle,
        type: KNOWLEDGE_TYPES.EXTERNAL_LINK,
      });
    },
    [ownerIds, tags, labels, subtitle],
  );

  const onFinishExternalUpload = React.useCallback(() => {
    setUploadExternalLink(null);
  }, []);

  const close = React.useCallback(() => {
    knowledgeSelectItem(null);
  }, [knowledgeSelectItem]);

  const filterByType = React.useCallback(
    thumbnail => {
      if (searchType === SEARCH_TYPES_KEYS.none) return false;
      return searchType !== SEARCH_TYPES_KEYS.documents ? thumbnail === '' : !!thumbnail;
    },
    [searchType],
  );

  // Derive filtered data from ordered items and search filters
  const filteredData = React.useMemo(() => {
    if (!searchTerm && !searchType) {
      return orderedItems;
    }

    return orderedItems.filter(
      ({ title, subtitle, thumbnail, keywords }) =>
        !filterByType(thumbnail) &&
        (normalizedContains(title, searchTerm) ||
          normalizedContains(subtitle, searchTerm) ||
          !!keywords?.some(key => normalizedContains(key, searchTerm))),
    );
  }, [orderedItems, searchTerm, searchType, filterByType]);

  // Expiry is applied before paging, so a page is 60 rows the user can see.
  const visibleData = React.useMemo(
    () => filteredData.filter(expiryFilter),
    [filteredData, expiryFilter],
  );

  // 800-row binders: render a page, then more as the user scrolls or asks.
  const { limit, remaining, showMore, sentinelRef } = useProgressiveList({
    total: visibleData.length,
    resetKey: `${id}|${searchTerm}|${searchType}`,
    pageSize: ROWS_PER_PAGE,
  });
  const { announce, liveRegion } = useLiveRegion();
  const langGeneral = useSelector(getLang('GENERAL'));
  const onShowMore = React.useCallback(() => {
    showMore();
    const left = Math.max(remaining - ROWS_PER_PAGE, 0);
    announce(lang.SHOW_MORE_ITEMS.replace('{count}', formatNumber(left)));
  }, [showMore, announce, lang.SHOW_MORE_ITEMS, remaining]);

  const { observeRef } = usePreloadVisibleContent({
    enabled: isOpen && !!id,
  });

  // Reset state when binder id changes
  React.useEffect(() => {
    if (id) {
      setOpenState(true);
      setLocalOrder(null); // Reset to use knowledgeItems
      setSearchTerm('');
      setSearchType(SEARCH_TYPES_KEYS.none);
    }
  }, [id, knowledgeItems]);

  const handleAfterOpenChange = React.useCallback(
    open => {
      if (open && !animated) {
        setAnimated(true);
      }
    },
    [animated],
  );

  React.useEffect(() => {
    const searchId = new URLSearchParams(search).get(routingParam);

    if (!searchId) {
      setOpenState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, routingParam]);

  const grouped = _.groupBy(visibleData.slice(0, limit), item =>
    item.thumbnail ? 'media' : 'documents',
  );

  const renderTags = () => {
    if (!tags?.contentType && !tags?.specialty) return null;

    return (
      <div className={styles.tagsContainer}>
        <TagsIcons tags={tags} hideTags={{ targetAudience: true, contentType: true }} />
        <TextTag tag={tags?.contentType} />
      </div>
    );
  };

  const renderList = (filtered, dndGroup = '', separatorDropRef) => {
    // Boundary between source-derived (above) and user-added (below) in a mirror binder.
    const separatorIndex = mirroredFrom ? filtered.findIndex(item => !item.isSourceDerived) : -1;
    const showSeparator = separatorIndex > 0 && separatorIndex < filtered.length;

    return filtered.flatMap((item, index) => {
      const node = renderItem(item, dndGroup);
      if (showSeparator && index === separatorIndex) {
        return [
          <LocalItemsSeparator
            key={`local-items-separator-${dndGroup}`}
            label={separatorLabel}
            dropRef={separatorDropRef}
          />,
          node,
        ];
      }
      return [node];
    });
  };

  const renderItem = (item, dndGroup) => {
    if (item.linked) {
      return (
        <KnowledgeWrapperComponent
          key={`${item.id}-link`}
          item={item}
          linkOwnerIds={item.linkOwnerIds}
          categoryId={id}
          adminMode={adminMode}
          onDelete={onDeleteLink}
          onUpdate={onUpdateLink}
          onMove={onMoveLink}
          linkedItem
          allowMove
          observeRef={observeRef}
          dragInGroupActive={draggingGroup === dndGroup}
          {...{
            dragItem,
            findItem,
            dndGroup,
            finishDrag,
          }}
        />
      );
    }

    return (
      <KnowledgeWrapperComponent
        item={item}
        key={item.id}
        categoryId={id}
        onDelete={deleteKnowledgeAction}
        onUpdate={editOrCreateKnowledge}
        onMove={moveKnowledgeAction}
        adminMode={adminMode}
        allowMove
        observeRef={observeRef}
        dragInGroupActive={draggingGroup === dndGroup}
        {...{
          dragItem,
          findItem,
          dndGroup,
          finishDrag,
        }}
        {...itemConfig}
      />
    );
  };

  return (
    <>
      {liveRegion}
      <ContentBinderDrawer
        open={isOpen}
        onClose={close}
        title={title}
        animated={animated}
        afterOpenChange={handleAfterOpenChange}
        icon={
          showExternalSync && getExternalSyncIcon(externalSyncProvider) ? (
            <img
              className={styles.externalSyncIcon}
              src={getExternalSyncIcon(externalSyncProvider)}
              alt={externalSyncProvider}
            />
          ) : null
        }
        subtitle={
          showExternalSync ? (
            <p className={styles.disclaimer}>{lang.EXTERNAL_SYNC_DISCLAIMER}</p>
          ) : null
        }
        badge={
          <CategoryMirrorBadge
            adminMode={adminMode}
            isCollaboratorUser={isCollaboratorUser}
            mirroredFrom={mirroredFrom}
            mirroredFromInfo={mirroredFromInfo}
            mirroredInTargets={mirroredInTargets}
            mirrorLang={mirrorLang}
            className={styles.mirrorBadge}
            iconClassName={styles.mirrorBadgeIcon}
          />
        }
        shareBoxProps={{
          path: sharePath ?? `${ROUTES.KNOWLEDGE}?${routingParam}=${id}`,
          id,
          title,
          tags,
          entityType: CATEGORY_TYPES.KNOWLEDGE,
        }}>
        <div className={styles.tagsAndOwnersWrapper}>
          {shouldShowField(KNOWLEDGE_VISIBILITY_FIELDS.OWNERS) && (
            <div className={styles.ownersWrapper}>
              <Owners ownerIds={ownerIds} title={langInfo.VALIDATED_BY} />
            </div>
          )}
          {renderTags()}
        </div>

        {loading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : loadError ? (
          <div className={styles.loadError}>
            <NotFound text={langGeneral.LOAD_FAILED} />
            <Button onClick={onRetry}>{langGeneral.RETRY}</Button>
          </div>
        ) : (
          <>
            <SearchBox
              size="large"
              onChange={e => setSearchTerm(e.target.value)}
              value={searchTerm}
            />

            <SearchType searchType={searchType} onChange={setSearchType} />

            {adminMode && !isReadOnly && (
              <div className={styles.buttons}>
                <KnowledgeAddDropdown
                  onCreate={createNewKnowledge}
                  onLink={linkKnowledge}
                  onUpload={onUploadFile}
                  onExternalLink={onUploadExternalLink}
                  title={title}
                  ownerIds={ownerIds}
                />
              </div>
            )}

            <div className={styles.knowledgeGroups}>
              {grouped.documents && (
                <div ref={documentsDropArea}>
                  <h4 className={styles.category}>{langTypes.DOCUMENTS}</h4>
                  <ListItemsNavigator orientation="vertical">
                    {renderList(grouped.documents, documentsAreaGroup, documentsSeparatorDrop)}
                  </ListItemsNavigator>
                </div>
              )}

              {grouped.media && (
                <div ref={mediaDropArea}>
                  <h4 className={styles.category}>{langTypes.MEDIA}</h4>
                  <ListItemsNavigator orientation="vertical">
                    {renderList(grouped.media, mediaAreaGroup, mediaSeparatorDrop)}
                  </ListItemsNavigator>
                </div>
              )}
              {remaining > 0 && (
                <div ref={sentinelRef} className={styles.showMore}>
                  <Button onClick={onShowMore}>
                    {lang.SHOW_MORE_ITEMS.replace('{count}', formatNumber(remaining))}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </ContentBinderDrawer>

      <KnowledgeDeletePopup
        open={!!deleteKnowledgeItem}
        entity={deleteKnowledgeItem}
        onFinish={onFinishDelete}
      />

      <KnowledgeMovePopup
        isOpen={!!knowledgeEntityMove}
        categoryId={id}
        item={knowledgeEntityMove}
        onFinish={onFinishMove}
        enableCreateLink
      />

      <KnowledgeLinkEditor
        open={!!linkedItemEditor}
        categoryId={id}
        onFinish={onFinishLink}
        parentOwnerIds={ownerIds}
      />

      <KnowledgeLinkDeletePopup
        open={!!deleteLink}
        entity={deleteLink}
        onDismiss={onFinishDeleteLink}
      />

      <KnowledgeLinkUpdatePopup
        open={!!updateLink}
        linkOwnerIds={updateLink?.linkOwnerIds}
        entity={updateLink}
        onDismiss={onFinishUpdateLink}
      />

      <KnowledgeLinkMovePopup
        isOpen={!!moveLink}
        categoryId={id}
        item={moveLink}
        onFinish={onFinishMoveLink}
      />

      <KnowledgeUploadFileComponent
        open={isUploadFileOpen}
        {...uploadFile}
        categoryId={id}
        ownerIds={ownerIds}
        close={onFinishUploadFile}
        editContent={editOrCreateKnowledge}
        editExternal={onExternalUpload}
        notificationApi={notificationApi}
      />

      <KnowledgeUploadExternalLinkComponent
        open={!!uploadExternalLink}
        {...(uploadExternalLink ?? {})}
        close={onFinishExternalUpload}
        editExternal={onExternalUpload}
      />
      {contextHolder}
    </>
  );
});

export { KnowledgeBinderComponent };
